import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "truline_welcome_tour_completed";

// Tour steps mapped to permissions — only shown if user has the permission.
const ALL_STEPS = [
  { perm: "jobs", route: "/jobs", title: "Jobs Dashboard", body: "Central hub for every calibration job. Track status from Estimate → In Shop → Completed → Invoiced. Click any card to open details." },
  { perm: "estimate_analyzer", route: "/estimates", title: "AI Estimate Analyzer", body: "Upload the body shop's CCC/Mitchell PDF and the AI extracts VIN, claim, insurance, line items, and auto-detects required ADAS operations." },
  { perm: "adas", route: "/adas", title: "ADAS Rules Engine", body: "Trim-aware rule engine that identifies missing calibrations from body shop estimates — no more losing revenue on unbilled operations." },
  { perm: "calibration_reports", route: "/calibration-reports", title: "Calibration Reports", body: "Generate the OEM-compliant PDF that goes back to the insurance carrier. Includes ALLDATA links, confidence scores, and evidence photos." },
  { perm: "invoices", route: "/invoices", title: "Invoicing", body: "Auto-generate invoices from Calibration Reports. Body-shop and Insurance dual invoicing supported (retail vs wholesale discount)." },
  { perm: "sales", route: "/sales/pipeline", title: "Sales Pipeline / CRM", body: "Kanban board with prospects moving through New → Contacted → Meeting → Proposal → Won. Automatically receives leads from the public pricing page." },
  { perm: "commissions", route: "/sales/commissions", title: "Sales Commissions", body: "When invoices are created and assigned to a sales rep, commission records are auto-generated and released when the invoice is paid." },
  { perm: "master_data", route: "/master/rate-tables", title: "Master Data & Pricing", body: "Rate tables, customers, body shops, insurers, technicians, vendors. Share your pricing publicly via the Rate Table share link." },
  { perm: "equipment", route: "/equipment", title: "Equipment & Targets", body: "Track calibration targets, scanners, cables. Get alerts when they need re-certification." },
  { perm: "work_orders", route: "/work-orders", title: "Mobile Work Orders", body: "The technician's mobile app. Pre/Post scans, live timer, evidence uploads — all synced to the calibration report." },
  { perm: "reports", route: "/reports", title: "Reports & Analytics", body: "Revenue by carrier, technician productivity, ADAS match rates, and P&L breakdowns." },
  { perm: "users", route: "/admin/pending-users", title: "User Management", body: "Approve new team members, assign roles + permissions, generate invite codes." },
];

export default function WelcomeTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    const key = `${STORAGE_KEY}_${user.id}`;
    if (!localStorage.getItem(key)) {
      // Delay 1.2s so it doesn't fight with initial page render
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [user]);

  const perms = user?.permissions || {};
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const steps = React.useMemo(() => {
    // Always include: welcome intro
    const list = [
      { perm: null, route: null, title: `Welcome, ${user?.name?.split(" ")[0] || "there"}!`,
        body: "This is TrueLine — your ADAS calibration ERP. Let me walk you through what you have access to. It'll take 30 seconds." },
    ];
    ALL_STEPS.forEach((s) => {
      if (isAdmin || perms[s.perm]) list.push(s);
    });
    list.push({ perm: null, route: null, title: "You're all set!",
      body: "Explore at your own pace. You can re-open this tour anytime from your profile menu." });
    return list;
  }, [user, perms, isAdmin]);

  const finish = () => {
    if (user) localStorage.setItem(`${STORAGE_KEY}_${user.id}`, "1");
    setOpen(false);
    setStep(0);
  };

  const next = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
      const s = steps[step + 1];
      if (s?.route) navigate(s.route);
    } else {
      finish();
    }
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!open || !user || steps.length === 0) return null;

  const s = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="welcome-tour">
      <div className="w-full max-w-lg bg-gradient-to-br from-[#0B1120] to-[#050810] border border-blue-500/40 rounded-2xl shadow-2xl overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-slate-800">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-blue-400 font-bold">
                  Guided tour · {step + 1}/{steps.length}
                </div>
                <h2 className="font-display text-xl font-semibold" data-testid="tour-title">{s.title}</h2>
              </div>
            </div>
            <button onClick={finish} data-testid="tour-close" className="text-slate-500 hover:text-slate-200 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-sm text-slate-300 leading-relaxed" data-testid="tour-body">
            {s.body}
          </p>

          {s.route && (
            <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2.5 text-[11px] text-emerald-200 font-mono">
              📍 {s.route}
            </div>
          )}

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-800">
            <Button
              variant="outline"
              onClick={prev}
              disabled={isFirst}
              className="border-slate-700 disabled:opacity-30"
              data-testid="tour-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-2">
              {!isLast && (
                <Button variant="ghost" onClick={finish} className="text-slate-500 hover:text-slate-300" data-testid="tour-skip">
                  Skip tour
                </Button>
              )}
              <Button
                onClick={next}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="tour-next"
              >
                {isLast ? "Get started" : "Next"} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
